
import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Collection } from "../types";
import { ensureValidCollectionName } from "../utils";

/**
 * Hook for fetching and managing collections data
 */
export const useCollectionsQuery = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  // Query for collections
  const { data: collections = [], isLoading } = useQuery({
    queryKey: ['collections', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      try {
        console.log("Fetching collections for user:", userId);
        const { data, error } = await supabase
          .from('collections')
          .select('*')
          .eq('user_id', userId)
          .order('position');
        
        if (error) {
          console.error('Error fetching collections:', error);
          toast.error('Failed to load collections');
          return [];
        }
        
        if (!data || !Array.isArray(data)) {
          console.error('Invalid data format from collections query:', data);
          return [];
        }
        
        // Debug the raw data we got from the database
        console.log("Raw collections data from database:", 
          data.map(c => ({ id: c.id, name: c.name, position: c.position, hidden: c.hidden }))
        );
        
        // Process each collection to ensure it has valid properties
        const processedData = data.map(collection => {
          return ensureValidCollectionName(collection);
        });
        
        // Log collection names after processing for debugging
        console.log("Collection names after processing:", 
          processedData.map(c => ({ id: c.id, name: c.name, position: c.position, hidden: c.hidden }))
        );
        
        // Always sort collections by position before returning to ensure consistency
        return [...processedData].sort((a, b) => a.position - b.position);
      } catch (e) {
        console.error("Unexpected error fetching collections:", e);
        return [];
      }
    },
    enabled: !!userId,
    staleTime: 0, // Never use stale data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true,
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchOnReconnect: true
  });

  // Function to find a collection by ID (critical for ensuring data consistency)
  const getCollectionById = useCallback((id: string): Collection | undefined => {
    if (!collections || !Array.isArray(collections)) return undefined;
    
    const collection = collections.find(c => c.id === id);
    if (!collection) return undefined;
    
    // Ensure the collection has a valid name
    return ensureValidCollectionName({...collection});
  }, [collections]);

  // Action to refresh collections
  const refreshCollections = useCallback(() => {
    console.log("Manually refreshing collections");
    return queryClient.invalidateQueries({ queryKey: ['collections', userId] });
  }, [queryClient, userId]);

  // Update collection position
  const updateCollectionPosition = useCallback(async (
    collectionId: string, 
    newPosition: number
  ) => {
    if (!userId || !collections || !Array.isArray(collections)) return false;
    
    try {
      console.log(`Updating collection ${collectionId} to position ${newPosition}`);
      
      // Get the collection to move
      const collectionToMove = collections.find(c => c.id === collectionId);
      if (!collectionToMove) {
        console.error("Collection not found:", collectionId);
        return false;
      }
      
      const oldPosition = collectionToMove.position;
      
      // If position is the same, do nothing
      if (oldPosition === newPosition) return true;
      
      // Batch update all affected collections' positions
      if (oldPosition < newPosition) {
        // Moving down - shift collections between old and new positions up by 1
        for (const collection of collections) {
          if (collection.position > oldPosition && collection.position <= newPosition) {
            const { error } = await supabase
              .from('collections')
              .update({ position: collection.position - 1 })
              .eq('id', collection.id);
              
            if (error) {
              console.error("Error updating collection position:", error);
              toast.error("Failed to reorder collections");
              return false;
            }
          }
        }
      } else {
        // Moving up - shift collections between new and old positions down by 1
        for (const collection of collections) {
          if (collection.position >= newPosition && collection.position < oldPosition) {
            const { error } = await supabase
              .from('collections')
              .update({ position: collection.position + 1 })
              .eq('id', collection.id);
              
            if (error) {
              console.error("Error updating collection position:", error);
              toast.error("Failed to reorder collections");
              return false;
            }
          }
        }
      }
      
      // Update the moved collection's position
      const { error } = await supabase
        .from('collections')
        .update({ position: newPosition })
        .eq('id', collectionId);
        
      if (error) {
        console.error("Error updating collection position:", error);
        toast.error("Failed to reorder collection");
        return false;
      }
      
      // Refresh collections after reordering
      await refreshCollections();
      toast.success("Collections reordered successfully");
      return true;
    } catch (e) {
      console.error("Error updating collection position:", e);
      toast.error("An unexpected error occurred");
      return false;
    }
  }, [userId, collections, refreshCollections]);

  // Always ensure sorted collections are returned
  const sortedCollections = [...collections].sort((a, b) => a.position - b.position);
  
  return {
    collections: sortedCollections, 
    isLoading,
    refreshCollections,
    getCollectionById,
    updateCollectionPosition
  };
};
